import React, { FunctionComponent, useContext, useMemo, useState } from "react";
import { 
  Typography, 
  Collapse, 
  IconButton, 
  TableCell, 
  TableRow 
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import styles from "./NewTickets.module.css";
import DynamicTable, { ColumnDef } from "../../components/shared-components/table/DynamicTable";
import { NewListTicket } from "./NewTicketsTypes";
import moment from "moment-timezone";
import { globalContext } from "../../Provider/AppProvider";
import { newTicketsEventHeaderLabelForSuperUser } from "./newTicketsEventHeaderLabel";

// Helper for status colors for Unsold Tab
const getUnsoldStatusColor = (status: string) => {
  const normalizedStatus = status?.toLowerCase();
  if (normalizedStatus?.includes('unsold')) {
    return { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' }; // Grey bg, dark grey text
  } else {
    return { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' }; // Default Grey
  }
};

const formatTicketDateTime = (dateStr: string) => {
    if (!dateStr) return { date: '-', time: '-' };
    const dt = moment.utc(dateStr).tz('America/Chicago');
    return {
        date: dt.format("MM/DD/YYYY"),
        time: dt.format("h:mm A")
    };
};

interface NewUnsoldTicketsProps {
    tickets: NewListTicket[];
    sortColumn?: string | null;
    sortDirection?: "asc" | "desc";
    onRequestSort?: (columnId: string) => void;
}

const NewUnsoldTickets: FunctionComponent<NewUnsoldTicketsProps> = ({
  tickets,
  sortColumn = null,
  sortDirection = "asc",
  onRequestSort,
}) => {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const { globalState } = useContext<any>(globalContext);

  const unsoldTicketColumns = useMemo((): ColumnDef[] => {
    const eventLabel = newTicketsEventHeaderLabelForSuperUser(
      globalState?.profile?.userRole
    );
    return [
      { id: "expand", label: "", className: styles.headerCellExpand },
      {
        id: "eventName",
        label: eventLabel,
        style: { color: "rgb(198 198 231)" },
        className: styles.headerCell,
      },
      {
        id: "section",
        label: "SECTION",
        style: { color: "rgb(198 198 231)" },
        className: styles.headerCell,
      },
      {
        id: "row",
        label: "ROW",
        style: { color: "rgb(198 198 231)" },
        className: styles.headerCell,
      },
      {
        id: "seat",
        label: "SEAT #S",
        style: { color: "rgb(198 198 231)" },
        className: styles.headerCell,
      },
      {
        id: "status",
        label: "STATUS",
        style: { color: "rgb(198 198 231)", textAlign: "center" },
        className: `${styles.headerCell} ${styles.headerCellCenter}`,
      },
    ];
  }, [globalState?.profile?.userRole]);

  const toggleRow = (id: string) => {
    setExpandedRows(prev => 
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  return (
    <DynamicTable
      columns={unsoldTicketColumns}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onRequestSort={onRequestSort}
    >
            {tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={unsoldTicketColumns.length} className={styles.emptyTableMessage}>
                  No Data Found
                </TableCell>
              </TableRow>
            ) : tickets.map((ticket) => {
                const rowId = ticket?.grouped_ticket_data?.[0]?.tp_id;
                const isExpanded = expandedRows.includes(rowId);
                const statusStyle = getUnsoldStatusColor(ticket.tp_status);

                // Format date and time
                const { date, time } = formatTicketDateTime(ticket.event_date_time);

                return (
                    <React.Fragment key={rowId}>
                        <TableRow 
                            className={`${styles.tableRow} ${isExpanded ? styles.rowExpanded : ''}`}
                            onClick={() => toggleRow(rowId)}
                        >
                            <TableCell className={styles.bodyCell}>
                                <IconButton size="small">
                                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                            </TableCell>
                            <TableCell className={styles.bodyCell} component="th" scope="row">
                                <Typography variant="subtitle2" className={styles.eventName}>{ticket.event_name}</Typography>
                                <Typography variant="caption" className={styles.eventDate}>
                                    {date} • {time}
                                </Typography>
                            </TableCell>
                            <TableCell className={styles.bodyCell}>{ticket.section}</TableCell>
                            <TableCell className={styles.bodyCell}>{ticket.row}</TableCell>
                            <TableCell className={styles.bodyCell}>{ticket.seat_range}</TableCell>
                            <TableCell className={styles.bodyCell} align="center">
                                <span className={styles.statusPill} style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, borderColor: statusStyle.border }}>
                                    {ticket.tp_status}
                                </span>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell style={{ padding: 0, border: 'none' }} colSpan={6}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <div className={styles.expandedContent}>
                                        <div className={styles.expandedHeader}>
                                            <div className={styles.ticketGroupDetails}>
                                                <Typography variant="caption" className={styles.detailsLabel}>Ticket Group Details</Typography>
                                                <div className={styles.detailsInfo}>
                                                    <strong>Section {ticket.section}</strong> <span className={styles.greyDotText}>•</span> <strong>Row {ticket.row}</strong> <span className={styles.greyDotText}>•</span> <span className={styles.greyText}>Seats {ticket.seat_range}</span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className={styles.seatsContainer}>
                                            {ticket.grouped_ticket_data.map(seat => {
                                                const seatStatusStyle = getUnsoldStatusColor(seat.tp_status);
                                                return (
                                                    <div 
                                                        key={seat.tp_id} 
                                                        className={styles.seatCard}
                                                        style={{ cursor: 'default' }}
                                                    >
                                                        <div className={styles.seatLeft}>
                                                            <Typography variant="body2" className={styles.seatLabel}>Seat {seat.seat_no}</Typography>
                                                            <Typography 
                                                                variant="caption" 
                                                                className={styles.seatStatus}
                                                                style={{ color: seatStatusStyle.color }}
                                                            >
                                                                {seat.tp_status}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </Collapse>
                            </TableCell>
                        </TableRow>
                    </React.Fragment>
                );
            })}
    </DynamicTable>
  );
};

export default NewUnsoldTickets;
